<script setup lang="ts">
import type {
  PublicWorkerProfile,
  PublicWorkerReviewsResponse,
  PublicWorkerService,
  PublicWorkerServicesResponse,
} from "~/types/public-worker";
import { getCategorySearchPath, toTitleCase } from "~/utils/search-catalog";
import {
  formatPersonName,
  getWorkerProfilePath,
  parseWorkerProfileSlug,
} from "~/utils/worker-profile";

const SEO_LOCALES = [
  { code: "lt", language: "lt-LT", ogLocale: "lt_LT" },
  { code: "en", language: "en-US", ogLocale: "en_US" },
  { code: "ru", language: "ru-RU", ogLocale: "ru_RU" },
] as const;

const route = useRoute();
const localeRoute = useLocaleRoute();
const runtimeConfig = useRuntimeConfig();
const requestUrl = useRequestURL();
const { t, locale } = useI18n();
const requestHeaders = import.meta.server
  ? useRequestHeaders(["cookie", "authorization"])
  : undefined;

const parsedRoute = parseWorkerProfileSlug(String(route.params.slug ?? ""));

if (
  !parsedRoute ||
  !Number.isSafeInteger(parsedRoute.id) ||
  parsedRoute.id <= 0
) {
  throw createError({ statusCode: 404, statusMessage: "Worker not found" });
}

const workerId = parsedRoute.id;
const { data: pageData, error } = await useAsyncData(
  `public-worker-${workerId}`,
  async () => {
    const [worker, services, reviews] = await Promise.all([
      $fetch<PublicWorkerProfile>(`/api/workers/${workerId}`),
      $fetch<PublicWorkerServicesResponse>(
        `/api/workers/${workerId}/services`,
        {
          query: { limit: 6, includeMetadata: true },
        },
      ),
      $fetch<PublicWorkerReviewsResponse>(`/api/workers/${workerId}/reviews`, {
        headers: requestHeaders,
        query: { page: 1, pageSize: 6 },
      }),
    ]);

    return { worker, services, reviews };
  },
);

if (error.value || !pageData.value) {
  const fetchError = error.value as {
    statusCode?: number;
    status?: number;
    statusMessage?: string;
    response?: { status?: number };
  } | null;
  const statusCode =
    fetchError?.statusCode ??
    fetchError?.status ??
    fetchError?.response?.status ??
    500;

  throw createError({
    statusCode,
    statusMessage:
      statusCode === 404
        ? "Worker not found"
        : fetchError?.statusMessage || "Could not load worker profile",
  });
}

const worker = computed(() => pageData.value!.worker);
const servicesData = computed(() => pageData.value!.services);
const reviews = computed(() => pageData.value!.reviews);
const formattedFirstName = computed(() => formatPersonName(worker.value.name));
const formattedLastName = computed(() =>
  formatPersonName(worker.value.lastname),
);
const fullName = computed(() =>
  formatPersonName(worker.value.name, worker.value.lastname),
);
const canonicalPath = computed(() => getWorkerProfilePath(worker.value));
const canonicalSlug = computed(
  () => canonicalPath.value.split("/").at(-1) || "",
);

if (String(route.params.slug) !== canonicalSlug.value) {
  await navigateTo(localeRoute(canonicalPath.value) || canonicalPath.value, {
    redirectCode: 301,
    replace: true,
  });
}

function resolveSiteOrigin(value: unknown) {
  const configuredUrl = String(value || "").trim();

  if (!configuredUrl) {
    if (import.meta.dev) return requestUrl.origin;

    throw createError({
      statusCode: 500,
      statusMessage: "NUXT_PUBLIC_SITE_URL is required",
    });
  }

  try {
    return new URL(configuredUrl).origin;
  } catch {
    throw createError({
      statusCode: 500,
      statusMessage: "NUXT_PUBLIC_SITE_URL must be a valid absolute URL",
    });
  }
}

function catalogLabel(
  type: "category" | "services" | "region",
  value?: string | null,
) {
  if (!value) {
    return type === "region"
      ? t("worker_profile.lithuania")
      : t("worker_profile.professional");
  }

  const key = `${type}.${value}`;
  const translated = t(key);
  return translated === key ? toTitleCase(value) : translated;
}

function professionalCategoryLabel(value?: string | null) {
  if (!value) return t("worker_profile.professional");

  const key = `professional_category.${value}`;
  const translated = t(key);
  return translated === key ? catalogLabel("category", value) : translated;
}

function truncateSeoText(value: string, maxLength = 160) {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;

  const candidate = normalized.slice(0, maxLength - 1);
  const lastSpace = candidate.lastIndexOf(" ");
  const truncated = lastSpace > 0 ? candidate.slice(0, lastSpace) : candidate;

  return `${truncated.replace(/[.,;:!?-]+$/, "")}…`;
}

function serializeJsonLd(value: unknown) {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

function getOfferPriceData(service: PublicWorkerService) {
  if (service.priceFrom == null || service.pricingType === "negotiable") {
    return {};
  }

  const priceCurrency = service.priceCurrency || "EUR";

  if (service.pricingType === "range" && service.priceTo != null) {
    return {
      priceSpecification: {
        "@type": "PriceSpecification",
        minPrice: service.priceFrom,
        maxPrice: service.priceTo,
        priceCurrency,
      },
    };
  }

  if (service.pricingType === "from") {
    return {
      priceSpecification: {
        "@type": "PriceSpecification",
        minPrice: service.priceFrom,
        priceCurrency,
      },
    };
  }

  const unitCode = {
    hour: "HUR",
    m2: "MTK",
    meter: "MTR",
    unit: "C62",
  }[service.pricingUnit];

  if (unitCode && service.pricingType === "fixed") {
    return {
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: service.priceFrom,
        priceCurrency,
        referenceQuantity: {
          "@type": "QuantitativeValue",
          value: 1,
          unitCode,
        },
      },
    };
  }

  return { price: service.priceFrom, priceCurrency };
}

const siteUrl = resolveSiteOrigin(runtimeConfig.public.siteUrl);
const localizedCanonicalPath = computed(
  () => localeRoute(canonicalPath.value)?.path || canonicalPath.value,
);
const canonicalUrl = computed(
  () => `${siteUrl}${localizedCanonicalPath.value}`,
);
const currentLocale = computed(
  () => SEO_LOCALES.find(item => item.code === locale.value) || SEO_LOCALES[0],
);
const localizedUrls = computed(() =>
  SEO_LOCALES.map(item => {
    const path =
      localeRoute(canonicalPath.value, item.code)?.path || canonicalPath.value;

    return {
      ...item,
      url: `${siteUrl}${path}`,
    };
  }),
);

const categoryName = computed(() =>
  professionalCategoryLabel(worker.value.primaryCategoryName),
);
const categoryPageName = computed(() =>
  catalogLabel("category", worker.value.primaryCategoryName),
);
const primaryService = computed(() =>
  servicesData.value.items.find(
    item => item.categoryId === worker.value.primaryCategoryId,
  ),
);
const serviceName = computed(() =>
  catalogLabel("services", primaryService.value?.name),
);
const isNationwide = computed(() => worker.value.coverageType === "nationwide");
const regionName = computed(() =>
  isNationwide.value
    ? t("worker_profile.lithuania")
    : catalogLabel("region", worker.value.primaryRegionName),
);
const seoTitle = computed(() =>
  isNationwide.value
    ? t("worker_profile.seo_title_nationwide", {
        name: fullName.value,
        category: categoryName.value,
      })
    : t("worker_profile.seo_title", {
        name: fullName.value,
        category: categoryName.value,
        region: regionName.value,
      }),
);
const seoDescription = computed(() => {
  const description = isNationwide.value
    ? t("worker_profile.seo_description_nationwide", {
        name: fullName.value,
        service: serviceName.value,
        category: categoryPageName.value,
      })
    : t("worker_profile.seo_description", {
        name: fullName.value,
        service: serviceName.value,
        category: categoryPageName.value,
        region: regionName.value,
      });
  const rating = reviews.value.summary.count
    ? t("worker_profile.seo_rating_description", {
        rating: reviews.value.summary.average.toFixed(1),
        count: reviews.value.summary.count,
      })
    : "";

  return truncateSeoText([description, rating].filter(Boolean).join(" "));
});
const structuredServiceNames = computed(() =>
  [
    ...new Set(
      servicesData.value.items.map(service =>
        catalogLabel("services", service.name),
      ),
    ),
  ].slice(0, 3),
);
const structuredDescription = computed(() => {
  const services = new Intl.ListFormat(currentLocale.value.language, {
    style: "long",
    type: "conjunction",
  }).format(
    structuredServiceNames.value.length
      ? structuredServiceNames.value
      : [categoryPageName.value],
  );
  const description = isNationwide.value
    ? t("worker_profile.seo_person_description_nationwide", {
        name: fullName.value,
        category: categoryPageName.value,
        services,
      })
    : t("worker_profile.seo_person_description", {
        name: fullName.value,
        category: categoryPageName.value,
        region: regionName.value,
        services,
      });
  const rating = reviews.value.summary.count
    ? t("worker_profile.seo_rating_description", {
        rating: reviews.value.summary.average.toFixed(1),
        count: reviews.value.summary.count,
      })
    : "";

  return truncateSeoText([description, rating].filter(Boolean).join(" "), 500);
});
const imageUrl = computed(() => {
  const image = worker.value.profileImageUrl?.trim() || "";
  if (!image || image.startsWith("data:") || image.startsWith("blob:")) {
    return null;
  }

  try {
    return new URL(image, siteUrl).toString();
  } catch {
    return null;
  }
});
const defaultOgImageUrl = `${siteUrl}/images/default-user-og.jpg`;
const ogImageUrl = computed(() => imageUrl.value || defaultOgImageUrl);

const categoryPath = computed(() => {
  if (!worker.value.primaryCategoryName) return null;

  const path = getCategorySearchPath(worker.value.primaryCategoryName);
  return localeRoute(path)?.path || path;
});
const categoryRegionPath = computed(() => {
  if (
    isNationwide.value ||
    !worker.value.primaryCategoryName ||
    !worker.value.primaryRegionName
  ) {
    return null;
  }

  const path = getCategorySearchPath(
    worker.value.primaryCategoryName,
    worker.value.primaryRegionName,
  );
  return localeRoute(path)?.path || path;
});
const breadcrumbItems = computed(() => {
  const items = [
    { name: "Senta.lt", item: siteUrl },
    {
      name: t("search.page.workers"),
      item: `${siteUrl}${localeRoute("/workers")?.path || "/workers"}`,
    },
  ];

  if (categoryPath.value) {
    items.push({
      name: categoryPageName.value,
      item: `${siteUrl}${categoryPath.value}`,
    });
  }

  if (categoryRegionPath.value) {
    items.push({
      name: regionName.value,
      item: `${siteUrl}${categoryRegionPath.value}`,
    });
  }

  items.push({ name: fullName.value, item: canonicalUrl.value });

  return items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    ...item,
  }));
});
const areasServed = computed(() => {
  if (isNationwide.value) {
    return [{ "@type": "Country", name: t("worker_profile.lithuania") }];
  }

  return [...new Set([worker.value.primaryRegionName, ...worker.value.regions])]
    .filter((region): region is string => Boolean(region))
    .map(region => ({
      "@type": "City",
      name: catalogLabel("region", region),
    }));
});
const offers = computed(() => {
  const personId = `${canonicalUrl.value}#person`;

  return servicesData.value.items.map(service => ({
    "@type": "Offer",
    "@id": `${canonicalUrl.value}#offer-${service.id}`,
    url: `${canonicalUrl.value}#portfolio`,
    offeredBy: { "@id": personId },
    ...getOfferPriceData(service),
    itemOffered: {
      "@type": "Service",
      "@id": `${canonicalUrl.value}#service-${service.id}`,
      name: catalogLabel("services", service.name),
      category: catalogLabel("category", service.categoryName),
      ...(service.description ? { description: service.description } : {}),
      provider: { "@id": personId },
      ...(areasServed.value.length ? { areaServed: areasServed.value } : {}),
    },
  }));
});

const expertise = computed(() =>
  [
    categoryName.value,
    ...servicesData.value.categories.map(category =>
      catalogLabel("category", category.name),
    ),
    ...servicesData.value.items.map(service =>
      catalogLabel("services", service.name),
    ),
  ].filter((value, index, values) => value && values.indexOf(value) === index),
);
const structuredData = computed(() => {
  const profilePageId = `${canonicalUrl.value}#profile-page`;
  const personId = `${canonicalUrl.value}#person`;
  const breadcrumbsId = `${canonicalUrl.value}#breadcrumbs`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfilePage",
        "@id": profilePageId,
        url: canonicalUrl.value,
        name: seoTitle.value,
        description: seoDescription.value,
        inLanguage: currentLocale.value.language,
        dateCreated: worker.value.createdAt,
        mainEntity: { "@id": personId },
        breadcrumb: { "@id": breadcrumbsId },
      },
      {
        "@type": "Person",
        "@id": personId,
        identifier: String(worker.value.id),
        name: fullName.value,
        url: canonicalUrl.value,
        ...(imageUrl.value ? { image: imageUrl.value } : {}),
        description: structuredDescription.value,
        jobTitle: categoryName.value,
        hasOccupation: {
          "@type": "Occupation",
          name: categoryName.value,
          ...(areasServed.value.length
            ? { occupationLocation: areasServed.value }
            : {}),
        },
        ...(worker.value.languages.length
          ? { knowsLanguage: worker.value.languages }
          : {}),
        ...(expertise.value.length ? { knowsAbout: expertise.value } : {}),
        ...(offers.value.length ? { makesOffer: offers.value } : {}),
      },
      {
        "@type": "BreadcrumbList",
        "@id": breadcrumbsId,
        itemListElement: breadcrumbItems.value,
      },
    ],
  };
});

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  robots: "index, follow",
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogType: "profile",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
  ogImage: () => ogImageUrl.value,
  ogImageAlt: () => fullName.value,
  ogImageWidth: () => (imageUrl.value ? undefined : 1200),
  ogImageHeight: () => (imageUrl.value ? undefined : 630),
  profileFirstName: () => formattedFirstName.value,
  profileLastName: () => formattedLastName.value || undefined,
  twitterCard: "summary_large_image",
  twitterTitle: () => seoTitle.value,
  twitterDescription: () => seoDescription.value,
  twitterImage: () => ogImageUrl.value,
  twitterImageAlt: () => fullName.value,
});

useHead(() => ({
  htmlAttrs: {
    lang: currentLocale.value.language,
  },
  link: [
    { rel: "canonical", href: canonicalUrl.value },
    ...localizedUrls.value.map(item => ({
      rel: "alternate",
      hreflang: item.language,
      href: item.url,
    })),
    {
      rel: "alternate",
      hreflang: "x-default",
      href: localizedUrls.value[0].url,
    },
  ],
  script: [
    {
      type: "application/ld+json",
      key: "worker-profile-jsonld",
      innerHTML: serializeJsonLd(structuredData.value),
    },
  ],
}));
</script>

<template>
  <WorkerPublicProfilePage
    :worker="worker"
    :services-data="servicesData"
    :reviews="reviews"
  />
</template>
